import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import { useAuth } from '~/context/AuthProvider';
import { fetchAdminStats, fetchPhotoUpdates } from '~/services/dashboard';
import { IPicture } from '~/types/campaigns';
import CardDataStats from '~/components/Dashboard/Cards/CardDataStats';
import CardPhotoUpdate from '~/components/Dashboard/Cards/CardPhotoUpdate';
import NewlyOpenedCampaigns from '~/components/Dashboard/Cards/NewlyOpenedCampaigns';
import TopCampaigns from '~/components/Dashboard/Cards/TopCampaigns';
import UpcomingEvents from '~/components/Dashboard/Cards/UpcomingEvents';
import TopReferralSources from '~/components/Dashboard/Cards/TopReferralSources';
// import FeaturedCampaigns from '~/components/Dashboard/Cards/FeaturedCampaigns'; // Commented out - Featured Campaigns hidden
import UnmonitoredChanges from '~/components/Dashboard/Cards/UnmonitoredChanges';
import MyCampaignRequests from '~/components/Dashboard/Cards/MyCampaignRequests';
import PatientServicesCoordinatorDashboard from '~/components/Dashboard/PatientServicesCoordinatorDashboard';
import MedicalProfessionalDashboard from '~/components/Dashboard/MedicalProfessionalDashboard';
// import CardStaff from '~/components/Dashboard/Cards/CardStaff'; // Commented out - PSC stats disabled
import FinancialCoordinatorDashboard from '~/components/Dashboard/FinancialCoordinatorDashboard';

type PscStat = {
    person_id: number;
    role_id: number;
    group_name: string;
    name: string;
    campaigns_count: number;
    campaign_requests_count: number;
};

type DashboardStats = {
    todos: number;
    messages: number;
    campaigns: number;
    campaign_requests: number;
    dept_messages: number;
    psc_count: number;
    psc_stats: PscStat[];
};

// Cache configuration - set CACHE_ENABLED to false to disable caching
const CACHE_ENABLED = false;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEYS = {
    stats: 'dashboard_stats_cache_v4',
    photos: 'dashboard_photos_cache_v4',
    timestamp: 'dashboard_cache_timestamp_v4',
};

const Dashboard: React.FC = () => {
    const { person, signOut, user } = useAuth();
    const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
        todos: 0,
        messages: 0,
        campaigns: 0,
        campaign_requests: 0,
        dept_messages: 0,
        psc_count: 0,
        psc_stats: [],
    });
    const [photoUpdates, setPhotoUpdates] = useState<IPicture[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    // const pscStats = dashboardStats?.psc_stats; // Commented out - PSC stats disabled
    const initializingRef = useRef(false);
    
    // Check if user has administrator role
    const isAdministrator = user?.roles?.includes('administrator');

    const getCachedData = useCallback(() => {
        if (!CACHE_ENABLED) return null;

        try {
            const timestamp = localStorage.getItem(CACHE_KEYS.timestamp);
            if (!timestamp) return null;

            const cacheAge = Date.now() - parseInt(timestamp);
            if (cacheAge > CACHE_DURATION) {
                // Cache expired, clear it
                localStorage.removeItem(CACHE_KEYS.stats);
                localStorage.removeItem(CACHE_KEYS.photos);
                localStorage.removeItem(CACHE_KEYS.timestamp);
                return null;
            }

            const cachedStats = localStorage.getItem(CACHE_KEYS.stats);
            const cachedPhotos = localStorage.getItem(CACHE_KEYS.photos);

            if (cachedStats && cachedPhotos) {
                return {
                    stats: JSON.parse(cachedStats),
                    photos: JSON.parse(cachedPhotos),
                };
            }
        } catch (error) {
            console.error('Error reading cache: ', error);
        }

        return null;
    }, []);

    const setCachedData = useCallback((stats: DashboardStats, photos: IPicture[]) => {
        if (!CACHE_ENABLED) return;

        try {
            localStorage.setItem(CACHE_KEYS.stats, JSON.stringify(stats));
            localStorage.setItem(CACHE_KEYS.photos, JSON.stringify(photos));
            localStorage.setItem(CACHE_KEYS.timestamp, Date.now().toString());
        } catch (error) {
            console.error('Error setting cache: ', error);
        }
    }, []);

    useEffect(() => {
        // Prevent multiple simultaneous initializations
        if (initializingRef.current) {
            return;
        }

        const initializeData = async () => {
            initializingRef.current = true;
            setLoading(true);
            try {
                // Check for cached data first
                const cachedData = getCachedData();
                if (cachedData) {
                    setDashboardStats(cachedData.stats);
                    if (Array.isArray(cachedData.photos)) {
                        setPhotoUpdates(cachedData.photos);
                    }
                    setLoading(false);
                    return;
                }

                // No cache or cache expired, fetch fresh data in parallel
                // Only fetch photo updates for administrators (PSCs don't display them)
                const [statsResp, photoUpdatesResp] = await Promise.all([
                    fetchAdminStats(),
                    isAdministrator ? fetchPhotoUpdates() : Promise.resolve(null),
                ]);

                if (isAdministrator && photoUpdatesResp) {
                    if (photoUpdatesResp.detail) {
                        if (
                            photoUpdatesResp.detail === 'Invalid token.' ||
                            photoUpdatesResp.detail ===
                                'Authentication credentials were not provided.'
                        ) {
                            signOut();
                            navigate('/login');
                            return;
                        } else {
                            console.error(
                                'Error fetching photo updates: ',
                                photoUpdatesResp.detail
                            );
                        }
                    } else if (Array.isArray(photoUpdatesResp)) {
                        setPhotoUpdates(photoUpdatesResp);
                    }
                }

                setDashboardStats(statsResp);
            } catch (error) {
                console.error('Error initializing data: ', error);
            } finally {
                setLoading(false);
                initializingRef.current = false;
            }
        };

        initializeData();
    }, [getCachedData, navigate, setCachedData, signOut]);

    return (
        <>
            <div className='mb-6'>
                <h1 className='text-title-md2 font-semibold text-black dark:text-white'>
                    Welcome
                    {person?.first_name ? `, ${person?.first_name}` : ''}
                </h1>
            </div>

            {!user?.roles?.includes('medical professional') && (
                <section className='grid grid-cols-1 gap-x-7.5 md:grid-cols-2 xl:grid-cols-5'>
                    <CardDataStats
                        title='To Dos'
                        total={dashboardStats?.todos}
                        link='/messages'
                        color='text-[#00aeb3]'
                        borderColor='border-[#e5f7f7]'
                        bgColor='bg-[#e5f7f7]'
                        state={{ todo: true }}
                        loading={loading}
                    />
                    <CardDataStats
                        title='My Messages'
                        total={dashboardStats?.messages}
                        link='/messages'
                        color='text-[#FEBF3E]'
                        borderColor='border-[#FFF9ED]'
                        bgColor='bg-[#FFF9ED]'
                        state={{ myMessages: true }}
                        loading={loading}
                    />
                    <CardDataStats
                        title='My Campaigns'
                        total={dashboardStats?.campaigns}
                        link='/client-campaigns'
                        color='text-[#afc12d]'
                        borderColor='border-[#f8f9eb]'
                        bgColor='bg-[#f8f9eb]'
                        state={{ myCampaigns: true }}
                        loading={loading}
                    />
                    <CardDataStats
                        title='Unassigned CRs'
                        total={dashboardStats?.campaign_requests}
                        link='/campaign-requests?status=Unassigned'
                        color='text-[#f47d29]'
                        borderColor='border-[#fef2ea]'
                        bgColor='bg-[#fef2ea]'
                        loading={loading}
                    />
                    <CardDataStats
                        title='Dept Messages'
                        total={dashboardStats?.dept_messages}
                        link='/messages'
                        color='text-[#952d46]'
                        borderColor='border-[#f5eaec]'
                        bgColor='bg-[#f5eaec]'
                        state={{ deptMessages: true }}
                        loading={loading}
                    />
                </section>
            )}

            {user?.roles?.includes('medical professional') ? (
                <MedicalProfessionalDashboard />
            ) : user?.roles?.includes('patient services coordinator') && !isAdministrator ? (
                <PatientServicesCoordinatorDashboard
                    dashboardStats={dashboardStats}
                />
            ) : (
                <>
                    {/* COMMENTED OUT: My Staff section disabled due to expensive PSC stats query
                        This section displays PSC staff statistics but the underlying query
                        causes significant performance issues on the dashboard.
                    <section>
                        <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                            <h2 className='text-xl font-semibold text-black dark:text-white'>
                                My Staff ({dashboardStats?.psc_count})
                            </h2>

                            <Link
                                to='/staff'
                                className='flex items-center gap-2 text-primary'
                            >
                                View All <ArrowRightSvg />
                            </Link>
                        </div>

                        <div className='mb-7.5 grid grid-cols-12 gap-7.5'>
                            {pscStats?.map((pscStat, i) => {
                                return (
                                    <CardStaff
                                        key={i}
                                        campaign_requests_count={
                                            pscStat.campaign_requests_count
                                        }
                                        campaigns_count={
                                            pscStat.campaigns_count
                                        }
                                        name={pscStat.name}
                                    />
                                );
                            })}
                        </div>
                    </section>
                    */}

                    {isAdministrator && (
                        <section>
                            <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                                <h2 className='text-xl font-semibold text-black dark:text-white'>
                                    Photo Updates {photoUpdates.length > 0 && `(${photoUpdates.length})`}
                                </h2>

                                <Link
                                    to='/'
                                    className='flex items-center gap-2 text-primary'
                                >
                                    View All <ArrowRightSvg />
                                </Link>
                            </div>

                            <div className='mb-7.5 grid grid-cols-1 gap-7.5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'>
                                {photoUpdates.map((photoUpdate) => {
                                    return (
                                        <CardPhotoUpdate
                                            key={photoUpdate.id}
                                            cardTitle={
                                                photoUpdate.campaign_data
                                                    ?.patient_name
                                            }
                                            cardContent={''}
                                            cardImageSrc={
                                                photoUpdate.cropped_url_cache
                                            }
                                            link={`/campaigns/${photoUpdate.campaign_data?.campaign_id}`}
                                            photoConsent={photoUpdate.photoConsent}
                                        />
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <section className='mb-7.5 grid grid-cols-12 gap-7.5'>
                        {user?.roles?.includes('patient services coordinator') && (
                            <div className='col-span-12 xl:col-span-4'>
                                <MyCampaignRequests />
                            </div>
                        )}
                        <div className={`col-span-12 ${user?.roles?.includes('patient services coordinator') ? 'xl:col-span-8' : ''}`}>
                            <NewlyOpenedCampaigns />
                        </div>
                    </section>

                    <section className='grid grid-cols-12 gap-7.5'>
                        <div className={`col-span-12 mb-7.5 ${isAdministrator ? 'xl:col-span-8' : 'xl:col-span-12'}`}>
                            <TopCampaigns />
                        </div>

                        {isAdministrator && (
                            <div className='col-span-12 mb-7.5 xl:col-span-4'>
                                <TopReferralSources />
                            </div>
                        )}
                    </section>

                    {/* COMMENTED OUT: Featured Campaigns section hidden
                    <section className='grid grid-cols-12 gap-7.5'>
                        {isAdministrator && (
                            <div className='col-span-12 mb-7.5 xl:col-span-8'>
                                <FeaturedCampaigns />
                            </div>
                        )}

                        <div className={`col-span-12 mb-7.5 ${isAdministrator ? 'xl:col-span-4' : 'xl:col-span-12'}`}>
                            <UpcomingEvents events={upcomingEvents} loading={eventsLoading} />
                        </div>
                    </section>
                    */}

                    <section className='mb-7.5'>
                        <UpcomingEvents />
                    </section>

                    <UnmonitoredChanges />

                    {isAdministrator && <FinancialCoordinatorDashboard />}
                </>
            )}
        </>
    );
};

export default Dashboard;
